import { PlusOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppIconButton from '@nxsys/components/AppIconButton';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsDeleteIcon from '@nxsys/components/AppsDeleteIcon';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { employeeActions } from '@nxsys/store/slices/employee/reducer/employeeSlice';
import { GetNoteEmployeeListParams } from '@nxsys/store/slices/employee/types';
import { Icons } from '@nxsys/types/icons';
import { INote } from '@nxsys/types/models/note';
import { Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import AddNoteModal from './components/AddNoteModal';
import {
  useCreateNoteEmployee,
  useDeleteNoteEmployee,
  useUpdateNoteEmployee,
} from '@nxsys/hooks/api';
import { NoteValues } from './components/NoteForm';
import {
  CreateNoteEmployeeParams,
  UpdateNoteEmployeeParams,
} from '@nxsys/types/api';
import { showMessage } from '@nxsys/utils/message';
import EditNoteModal from './components/EditNoteModal';
import DetailNoteModal from './components/DetailNoteModal';
import { debounce } from 'lodash';


type NoteListingProps = {
  employeeId: string;
};

const NoteListing: React.FC<NoteListingProps> = ({ employeeId }) => {
  const { messages, formatMessage } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();

  const { noteList } = useSelector((state: AppState) => state.employeeStore);
  const { list, pagination, loading, queryParams } = noteList;

  const handleGetNoteList = (params: GetNoteEmployeeListParams) => {
    const newParams = {
      ...params,
      id: employeeId,
    };
    dispatch(employeeActions.getNoteEmployeeList(newParams));
  };

  const refetch = () => {
    handleGetNoteList({
      ...queryParams,
    });
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleGetNoteList({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleGetNoteList({
        ...queryParams,
        sortField: sorter.field || '',
        sortType: sorter.order,
      });
    } else {
      handleGetNoteList({
        ...queryParams,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  useEffect(() => {
    handleGetNoteList({ page: 0, size: 20 });
  }, []);

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      handleGetNoteList({
        ...queryParams,
        page: 0,
        size: 20,
        search: search,
      });
    }, 500),
    [queryParams],
  );

  const onChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    debouncedFetchData(value);
  };

  const columns: ColumnsType<INote> = [
    {
      key: 'actions',
      title: messages['common.action'],
      fixed: 'left',
      align: 'left',
      width: 130,
      render: (record) => (
        <ul className="flex items-center justify-start gap-2">
          <li>
            <AppIconButton
              title="View"
              icon={<Icons.IconEye />}
              onClick={() => onToggleDetailNoteModal(record)}
            />
          </li>
          <li>
            <AppIconButton
              title="Edit"
              icon={<Icons.IconEdit />}
              onClick={() => onToggleEditNoteModal(record)}
            />
          </li>
          <li>
            <AppsDeleteIcon
              deleteAction={() => handleDeleteNote(record.id)}
              title="Delete Document"
              paragraph={
                <p>
                  {formatMessage(
                    {
                      id: 'common.deleteNoteConfirmWithId',
                    },
                    { id: record.id },
                  )}
                </p>
              }
            />
          </li>
        </ul>
      ),
    },
    {
      key: 'title',
      title: messages['common.title'],
      dataIndex: 'title',
      width: 200,
    },
    {
      key: 'content',
      title: messages['common.description'],
      dataIndex: 'content',
      render: (value) => <div dangerouslySetInnerHTML={{ __html: value }} />,
    },
    {
      key: 'createdBy',
      title: messages['common.createdBy'],
      dataIndex: 'updatedBy',
      width: 200,
      render: (value) => (
        <span>{`${value?.forename || ''} ${value?.surname || ''}`}</span>
      ),
    },
    {
      key: 'createdOn',
      title: messages['common.createdOn'],
      dataIndex: 'createdAt',
      width: 200,
      render: (value) => (
        <span>
          {value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '_'}
        </span>
      ),
    },
  ];

  const [openAddNoteModal, setOpenAddNoteModal] = useState(false);
  const onToggleAddNoteModal = () => {
    setOpenAddNoteModal(!openAddNoteModal);
  };
  const { run: createNoteEmployee } = useCreateNoteEmployee();
  const handleAddNewNote = async (values: NoteValues) => {
    const params: CreateNoteEmployeeParams = {
      title: values.title,
      content: values.content,
    };
    const res = await createNoteEmployee(employeeId, params);
    if (res.success) {
      showMessage({
        type: 'success',
        content: messages['common.addSuccess'],
      });
      onToggleAddNoteModal();
      refetch();
    }
  };

  const [openEditNoteModal, setOpenEditNoteModal] = useState(false);
  const [noteSelected, setNoteSelected] = useState<INote | null>(null);
  const onToggleEditNoteModal = (data?: INote) => {
    setOpenEditNoteModal(!openEditNoteModal);
    setNoteSelected(data || null);
  };
  const { run: updateNoteEmployee } = useUpdateNoteEmployee();
  const handleUpdateNote = async (values: NoteValues) => {
    const params: UpdateNoteEmployeeParams = {
      title: values.title,
      content: values.content,
    };
    const res = await updateNoteEmployee(
      noteSelected?.id || '',
      employeeId,
      params,
    );
    if (res.success) {
      showMessage({
        type: 'success',
        content: messages['common.updateSuccess'],
      });
      onToggleEditNoteModal();
      refetch();
    }
  };

  const [openDetailNoteModal, setOpenDetailNoteModal] = useState(false);
  const onToggleDetailNoteModal = (data?: INote) => {
    setOpenDetailNoteModal(!openDetailNoteModal);
    setNoteSelected(data || null);
  };
  const { run: deleteNoteEmployee } = useDeleteNoteEmployee();
  const handleDeleteNote = async (id: string) => {
    const res = await deleteNoteEmployee(id, employeeId);
    if (res.success) {
      showMessage({
        type: 'success',
        content: messages['common.deleteSuccess'],
      });

      refetch();
    }
  };

  return (
    <div>
      <Typography.Title
        level={5}
        className="!mb-6 !text-[20px] !leading-[1.6] !tracking-[-0.02em] !text-[#1A1D1F]"
      >
        {messages['common.notes']}
      </Typography.Title>
      <div className="flex justify-between items-center mb-[16px]">
        <AppInputSearch onChange={onChangeSearch} />
        <AppButton
          type="primary"
          name={messages['common.addNew']}
          leftIcon={<PlusOutlined />}
          onClick={onToggleAddNoteModal}
        />
      </div>
      <AppsContent>
        <AppTableContainer
          data={list || []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 1000, y: 700 }}
          titleEmpty={messages[`notes.empty`]}
          columns={columns}
          onChangeTable={onChangeTable}
          onChangePagination={onChangePagination}
        />
      </AppsContent>
      <AddNoteModal
        isOpen={openAddNoteModal}
        toggle={onToggleAddNoteModal}
        onSubmit={handleAddNewNote}
      />
      <EditNoteModal
        isOpen={openEditNoteModal}
        toggle={onToggleEditNoteModal}
        onSubmit={handleUpdateNote}
        data={noteSelected}
      />
      <DetailNoteModal
        isOpen={openDetailNoteModal}
        toggle={onToggleDetailNoteModal}
        onUpdate={handleUpdateNote}
        onDelete={handleDeleteNote}
        data={noteSelected}
      />
    </div>
  );
};

export default NoteListing;
